<template>
  <ConfigPageShell
    title="Vehicle Groups"
    subtitle="Create and manage groups that organize your vehicles (e.g., New, Used, CPO, EV). A vehicle can belong to more than one group, and “All Vehicles” always includes every vehicle."
  >
    <template #actions>
      <v-tooltip text="Create a new custom group" location="bottom">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-plus"
            @click="openNewSegmentDialog()"
            :disabled="
              isLoading(segmentStore.loadingStatus.saveSegments) ||
              isLoading(segmentStore.loadingStatus.segments)
            "
          >
            Add Group
          </v-btn>
        </template>
      </v-tooltip>
      <v-btn
        v-if="!mdAndDown"
        color="primary"
        variant="flat"
        @click="saveSegments()"
        :loading="
          isLoading(segmentStore.loadingStatus.saveSegments) ||
          isLoading(segmentStore.loadingStatus.segments)
        "
        :disabled="
          props.isOnBoarding
            ? false
            : isLoading(segmentStore.loadingStatus.saveSegments) ||
              isLoading(segmentStore.loadingStatus.segments) ||
              !hasChanges
        "
      >
        {{ props.isOnBoarding ? 'Next' : 'Save' }}
      </v-btn>
    </template>

    <template #belowHeader>
        <div class="text-caption text-grey-darken-1 mb-6" style="font-style: italic">
          💡 Tip: Start with the default groups — add or edit only if you need additional categories
          for your listings or marketing.
        </div>
    </template>

        <v-data-table
          :headers="segmentStore.segmentHeaders"
          :items="orderedSegments"
          :loading="isLoading(segmentStore.loadingStatus.segments)"
          hide-default-header
          hide-default-footer
          expand-on-click
          :item-value="(item) => (item as any).id"
          v-model:expanded="expandedRowIds"
          :item-class="(item: any) => (item.id === 'all' ? 'all-vehicles-row' : '')"
        >
          <!-- Name cell (priority UI hidden) -->
          <template v-slot:item.name="{ item }">
            <div
              class="d-flex align-center segment-row"
              v-bind="props"
              :class="{ 'pulse-highlight': shouldPulse(item as any) }"
            >
              <span>{{ (item as any).name }}</span>
              <v-chip
                v-if="(item as any).custom"
                size="x-small"
                class="ml-2"
                color="info"
                variant="outlined"
              >
                custom
              </v-chip>
              <v-tooltip text="Total conditions for this segment">
                <template #activator="{ props: p2 }">
                  <v-chip v-bind="p2" size="x-small" class="ml-2" color="grey" variant="outlined">
                    {{ rulesCountLabel((item as any).id) }}
                  </v-chip>
                </template>
              </v-tooltip>
              <v-spacer />
              <div class="d-flex align-center">
                <!-- Priority UI hidden per request (acts like tags) -->
              </div>
            </div>
          </template>
          <!-- Expandable rules editor per segment -->
          <template v-slot:expanded-row="{ item, columns }">
            <td :colspan="columns.length">
              <div class="segment-block pa-4" :class="{ 'segment-alt': isEvenOrder(item as any) }">
                <div class="segment-header d-flex align-center justify-space-between mb-3 ga-3">
                  <div class="d-flex align-center ga-3 flex-wrap">
                    <div>
                    Group: <strong>{{ (item as any).name }}</strong>
                    <span v-if="(item as any).id !== 'all'" class="text-grey-darken-1 ml-2"
                      >#{{ getDisplayOrder(item as any) }}</span
                    >
                    <span v-else class="text-grey ml-2"
                      >(Fallback — applies when no other segment matches)</span
                    >
                  </div>
                    <v-select
                      v-if="criteriaFor(item.id).length > 1"
                      class="segment-logic-select"
                      :model-value="getSegmentLogic((item as any).id)"
                      :items="segmentLogicOptions"
                      item-title="title"
                      item-value="value"
                      density="compact"
                      variant="outlined"
                      hide-details
                      @update:modelValue="(v) => setSegmentLogic((item as any).id, v)"
                    />
                    <div v-else class="text-caption text-grey-darken-1">
                      Add 2+ conditions to choose AND/OR
                    </div>
                  </div>
                  <div v-if="(item as any).custom" class="d-flex align-center ga-2">
                    <v-chip size="x-small" color="info" variant="outlined">Custom</v-chip>
                    <v-btn
                      size="small"
                      variant="outlined"
                      prepend-icon="mdi-delete"
                      class="delete-btn"
                      @click="confirmDeleteSegment(item as any)"
                    >
                      Delete Segment
                    </v-btn>
                  </div>
                </div>
                <div
                  v-if="criteriaFor(item.id).length === 0 && item.id !== 'all'"
                  class="text-center py-4"
                  :class="(item as any).custom ? 'text-warning-darken-2' : 'text-grey-darken-1'"
                  style="font-style: italic"
                >
                  <div :class="(item as any).custom ? 'font-weight-medium' : ''">
                    No conditions yet. Add one to define which vehicles belong here.
                  </div>
                  <div class="mt-1 text-caption" style="font-style: normal">
                    <span v-if="!(item as any).custom">Inherits from All Vehicles</span>
                    <span v-else class="text-error"
                      >⚠️ Custom segments require at least one condition to be saved</span
                    >
                  </div>
                </div>
                <v-row v-else class="mb-2" v-for="(rule, idx) in criteriaFor(item.id)" :key="idx">
                  <!-- Field name -->
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="rule.field"
                      :items="availableFields"
                      density="compact"
                      variant="outlined"
                      hide-details
                      placeholder="Select field"
                      item-title="title"
                      item-value="value"
                    />
                  </v-col>
                  <!-- Conditional operator -->
                  <v-col cols="12" md="2">
                    <v-select
                      v-model="rule.operator"
                      :items="operatorOptions"
                      item-title="title"
                      item-value="value"
                      density="compact"
                      variant="outlined"
                      hide-details
                    >
                      <template #append-inner>
                        <v-tooltip :text="getOperatorHelp(rule.operator)">
                          <template #activator="{ props }">
                            <v-icon v-bind="props" size="18" class="ml-1 text-grey"
                              >mdi-help-circle-outline</v-icon
                            >
                          </template>
                        </v-tooltip>
                      </template>
                    </v-select>
                  </v-col>
                  <!-- Value -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="rule.value"
                      density="compact"
                      variant="outlined"
                      hide-details
                      placeholder='e.g. "new"'
                    />
                  </v-col>
                  <v-col cols="12" md="1" class="d-flex align-center">
                    <v-btn icon size="35" color="red" @click="removeCriteria(item.id, idx)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                  <v-col cols="12" class="pt-0 mt-n3 text-grey-darken-1 text-caption">
                    {{ previewCondition(rule) }}
                  </v-col>
                </v-row>
                <div class="text-center">
                  <v-btn
                    prepend-icon="mdi-plus"
                    variant="text"
                    color="primary"
                    size="small"
                    @click="addCriteria(item.id)"
                  >
                    Add New Condition
                  </v-btn>
                </div>
              </div>
            </td>
          </template>
        </v-data-table>
    </ConfigPageShell>

    <!-- Floating Save button (phones only) -->
    <v-fab-transition>
      <v-btn
        v-if="
          mdAndDown &&
          !isLoading(segmentStore.loadingStatus.segments) &&
          snapshotReady &&
          (props.isOnBoarding || hasChanges)
        "
        class="save-fab"
        color="primary"
        variant="elevated"
        size="large"
        :loading="isLoading(segmentStore.loadingStatus.saveSegments)"
        :disabled="isLoading(segmentStore.loadingStatus.segments)"
        @click="saveSegments()"
      >
        <v-icon start>mdi-content-save</v-icon>
        Save
      </v-btn>
    </v-fab-transition>

  <!-- New Segment Dialog -->
  <v-dialog v-model="newSegmentDialog.show" max-width="500px" persistent>
    <v-card>
      <v-card-title class="text-h6 font-weight-bold"> Create New Segment </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="newSegmentDialog.name"
          label="Segment Name"
          placeholder="e.g., Luxury Vehicles"
          variant="outlined"
          autofocus
          :error-messages="newSegmentDialog.error"
          @keyup.enter="createNewSegment()"
          @input="newSegmentDialog.error = ''"
        />
        <div class="text-caption text-grey-darken-1 mt-n2">
          Enter a descriptive name for this segment. You can add conditions after creating it.
        </div>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" @click="closeNewSegmentDialog()"> Cancel </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="createNewSegment()"
          :disabled="!newSegmentDialog.name.trim()"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Segment Confirmation Dialog -->
  <v-dialog v-model="deleteSegmentDialog.show" max-width="500px" persistent>
    <v-card>
      <v-card-title class="text-h6 font-weight-bold"> Delete Segment </v-card-title>
      <v-card-text>
        <p>
          Are you sure you want to delete the segment
          <strong>"{{ deleteSegmentDialog.segmentName }}"</strong>?
        </p>
        <p class="text-caption text-grey-darken-1 mt-2">
          This action cannot be undone. Any vehicles matching this segment will fall back to other
          segments or "All Vehicles".
        </p>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" @click="closeDeleteSegmentDialog()"> Cancel </v-btn>
        <v-btn color="error" variant="flat" @click="deleteSegment()"> Delete </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import ConfigPageShell from '@/components/config/ConfigPageShell.vue'
import _ from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useSegmentStore } from '@/stores/segment'
import { InventorySources } from '@/types'
import { isLoading, makeAlert } from '@/utils'
import { usePublishStore } from '@/stores/usePublishStore'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import { deriveRulesetLogicFromCriteria, normalizeSegmentLogic } from '@/utils/segmentRuleset'
import { useRuleConditionFieldsStore } from '@/stores/ruleConditionFields'
import {
  withLegacyUsedFields,
  type RuleConditionFieldOption,
} from '@/utils/ruleConditionFields'
import { canonicalizeCurrentSegments, canonicalizePublishedSegments } from '@/utils/segmentCanonical'

const segmentStore = useSegmentStore()
const publishStore = usePublishStore()
const accountSettings = useAccountSettingsStore()
const ruleFieldsStore = useRuleConditionFieldsStore()
const productSettings = useProductSettingsStore()
const appConfig = useJazelAppConfig()

const props = defineProps<{
  isOnBoarding?: boolean
}>()

const emit = defineEmits<{
  (e: 'done'): void
}>()

const state = ref<{
  selectedSegment: string | null
}>({
  selectedSegment: 'all',
})

const newSegmentDialog = ref({
  show: false,
  name: '',
  error: '',
})

const deleteSegmentDialog = ref({
  show: false,
  segmentId: '',
  segmentName: '',
})

// Track initial snapshot of segments to determine if there are changes
const initialSegmentsSnapshot = ref<string>('')
const snapshotReady = ref(false)
const hasChanges = computed(() => {
  if (!snapshotReady.value) return false
  try {
    const current = JSON.stringify(segmentStore.segments)
    return current !== initialSegmentsSnapshot.value
  } catch (_e) {
    return false
  }
})

// Available fields from 'all' segment mapping
const availableFields = ref<RuleConditionFieldOption[]>([])
// Operator options with friendly labels
const operatorOptions = [
  { title: 'Includes', value: 'contains' },
  { title: 'Equals', value: 'equals' },
  { title: 'Is Missing', value: 'missing' },
  { title: 'Does Not Include', value: 'not_contains' },
  { title: 'Starts With', value: 'starts_with' },
  { title: 'Ends With', value: 'ends_with' },
  { title: 'In List', value: 'in' },
  { title: 'Not In List', value: 'not_in' },
]

// Writable computed so we can reorder locally and write back to the store
const segments = computed({
  get: () => segmentStore.segments as any,
  set: (v) => {
    ;(segmentStore as any).segments = v as any
  },
})
const expandedRowIds = ref<string[]>([])
const { smAndDown, mdAndDown } = useDisplay()
const orderedSegments = computed(() => {
  const nonAll = [...segments.value]
    .filter((s: any) => s.id !== 'all')
    .sort((a: any, b: any) => (a.priority ?? 999) - (b.priority ?? 999))
  const allSeg = segments.value.find((s: any) => s.id === 'all')
  return allSeg ? [...nonAll, allSeg] : nonAll
})
const criteria = computed(
  () =>
    _.find(segmentStore.segments, (item) => item.id === state.value.selectedSegment)?.criteria ??
    [],
)
// Helpers for expanded-row editor
const criteriaFor = (segmentId: string) => {
  const found = _.find(segmentStore.segments, (item) => item.id === segmentId)
  return (found?.criteria as any[]) || []
}
const addCriteria = (segmentId: string) => {
  const idx = _.findIndex(segmentStore.segments, { id: segmentId })
  if (idx > -1) {
    const currentLogic = deriveRulesetLogicFromCriteria(segmentStore.segments[idx].criteria as any, 'AND')
    segmentStore.segments[idx].criteria.push({
      field: '',
      operator: 'contains',
      value: '',
      logicalOperator: currentLogic,
    } as any)
  }
}
const removeCriteria = (segmentId: string, index: number) => {
  const idx = _.findIndex(segmentStore.segments, { id: segmentId })
  if (idx > -1) {
    segmentStore.segments[idx].criteria.splice(index, 1)
  }
}

// Backend supports a single segment-level ruleset.logic; keep connectors consistent in the UI.
const setSegmentLogic = (segmentId: string, logic: unknown) => {
  const normalized = normalizeSegmentLogic(logic, 'AND')
  const idx = _.findIndex(segmentStore.segments, { id: segmentId })
  if (idx > -1) {
    const crit = segmentStore.segments[idx].criteria as any[]
    if (Array.isArray(crit)) {
      for (const c of crit) c.logicalOperator = normalized
    }
  }
}

const getSegmentLogic = (segmentId: string) => {
  const found = _.find(segmentStore.segments, (item) => item.id === segmentId) as any
  return deriveRulesetLogicFromCriteria(found?.criteria as any, 'AND')
}

const segmentLogicOptions = [
  { title: 'All conditions (AND)', value: 'AND' },
  { title: 'Any condition (OR)', value: 'OR' },
]
const selectedSegment = computed(() =>
  _.find(segmentStore.segments, (item) => item.id === state.value.selectedSegment),
)

const rulesCountLabel = (segmentId: string) => {
  const found = _.find(segmentStore.segments, (item) => (item as any).id === segmentId) as any
  const count = Array.isArray(found?.criteria) ? found.criteria.length : 0
  return count === 1 ? '1 rule' : `${count} rules`
}

// Pulse highlight on first load for the top segment to hint interactivity
const hasPulsed = ref(false)
onMounted(() => {
  // allow UI to paint, then remove pulse after a moment
  setTimeout(() => {
    hasPulsed.value = true
  }, 1800)
})

function shouldPulse(item: any): boolean {
  if (hasPulsed.value) return false
  const nonAll = (segments.value as any[]).filter((s) => s.id !== 'all')
  return nonAll.length > 0 && nonAll[0]?.id === (item as any).id
}

const saveSegments = async () => {
  // Validate: Custom segments must have at least one condition
  const customSegments = (segments.value as any[]).filter((s) => s.custom === true)
  const emptyCustomSegments = customSegments.filter((s) => !s.criteria || s.criteria.length === 0)

  if (emptyCustomSegments.length > 0) {
    const segmentNames = emptyCustomSegments.map((s) => `"${s.name}"`).join(', ')
    makeAlert(
      `Custom segments must have at least one condition: ${segmentNames}. Please add conditions or delete these segments.`,
      'error',
    )
    return
  }

  const response = await segmentStore.saveSegments(props.isOnBoarding)

  if (props.isOnBoarding) {
    emit('done')
    return
  }

  if (response) {
    makeAlert('Segments saved as draft. Click "Publish Changes" to apply to live inventory.', 'success')

    // IMPORTANT: Reconcile existing unpublished changes against latest ISE processing
    // This clears any old unpublished markers that have been published in the background
    const feedsEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
    await publishStore.checkAndReconcile(feedsEndpoint, accountSettings.accountId)

    // Now compare against published snapshot (smart comparison)
    const { compareSegments } = await import('@/utils/compareConfigs')

    // IMPORTANT: use authoritative published baseline from backend (published-only),
    // not a potentially-stale local snapshot.
    const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
    const jazelId = accountSettings.accountId

    let publishedSegmentsRaw: any = null
    try {
      const { fetchSegmentationConfig } = await import('@/services/segmentationConfigService')
      const publishedResp = await fetchSegmentationConfig(iseEndpoint, jazelId /* includeDraft=false */)
      publishedSegmentsRaw = publishedResp?.data?.segments || null
    } catch (e) {
      console.warn('⚠️ Failed to fetch published segmentation baseline; falling back to cached snapshot', e)
      publishedSegmentsRaw = null
    }

    // Best-effort fallback (may be stale, but better than treating everything as added)
    if (!publishedSegmentsRaw) {
      publishedSegmentsRaw = productSettings.publishedSnapshot?.segments || null
    }

    const publishedCanonical = canonicalizePublishedSegments(publishedSegmentsRaw)
    const currentCanonical = canonicalizeCurrentSegments(segmentStore.segments as any[])

    const comparison = compareSegments(publishedCanonical, currentCanonical)

    // Only mark dirty if there are actual changes from published state
    if (comparison.hasChanges) {
      const changes: any = {
        total: comparison.total,
        timestamp: new Date().toISOString(),
      }

      if (comparison.added.length > 0) {
        changes.added = comparison.added
      }
      if (comparison.modified.length > 0) {
        changes.modified = comparison.modified
      }
      if (comparison.removed.length > 0) {
        changes.removed = comparison.removed
      }

      publishStore.markDirty('segments', changes)
      console.log('✏️ Segments marked dirty - net changes:', comparison)
    } else {
      console.log('✅ No net changes - segments match published state')
      // NOTE: No unpublished marker persistence. Dirty state is derived from server-truth (drafts).
    }

    // Reload segments from backend to refresh UI with saved data
    await segmentStore.getSegments({ includeDraft: true })
    try {
      initialSegmentsSnapshot.value = JSON.stringify(segmentStore.segments)
      snapshotReady.value = true
    } catch (_e) {
      snapshotReady.value = true
    }
  } else {
    makeAlert('An error occurred', 'error')
  }
}

// Field names already referenced by saved segment criteria — used ONLY to keep
// existing selections from rendering blank (display-only legacy entries), never
// as the source of which fields are available.
const collectUsedFields = (): string[] => {
  const used = new Set<string>()
  for (const seg of (segmentStore.segments as any[]) || []) {
    for (const r of (seg?.criteria as any[]) || []) {
      const v = String(r?.field || '').trim()
      if (v) used.add(v)
    }
  }
  return Array.from(used)
}

// Load the deliberate, server-owned rule-condition field catalog (shared with
// Publish Rules, via the shared store). Availability is intentional and
// account-independent — it does NOT depend on the dealer's feeds or on which
// fields appear in saved segments.
const fetchAvailableFields = async () => {
  await ruleFieldsStore.load()
  const catalog: RuleConditionFieldOption[] = ruleFieldsStore.options
  availableFields.value = withLegacyUsedFields(catalog, collectUsedFields())
}

const getEssentialData = async () => {
  // Ensure segments are loaded before building the dropdown so we can preserve any in-use legacy fields.
  await segmentStore.getSegments({ includeDraft: true })
  await fetchAvailableFields()
  try {
    initialSegmentsSnapshot.value = JSON.stringify(segmentStore.segments)
    snapshotReady.value = true
  } catch (_e) {
    initialSegmentsSnapshot.value = ''
    snapshotReady.value = true
  }
}

onMounted(() => {
  getEssentialData()
})

// Arrow-based reordering
const moveRow = (id: string, direction: 'up' | 'down') => {
  const nonAll = (segments.value as any[]).filter((s) => s.id !== 'all')
  const from = nonAll.findIndex((s) => s.id === id)
  if (from === -1) return

  const to = direction === 'up' ? Math.max(0, from - 1) : Math.min(nonAll.length - 1, from + 1)
  if (to === from) return

  const [moved] = nonAll.splice(from, 1)
  nonAll.splice(to, 0, moved)

  const allSeg = (segments.value as any[]).find((s) => s.id === 'all')
  segments.value = (allSeg ? [...nonAll, allSeg] : nonAll) as any

  // Re-number priorities to 10/20/30…, keep All = 1000
  nonAll.forEach((s, idx) => {
    ;(s as any).priority = (idx + 1) * 10
  })
  if (allSeg) (allSeg as any).priority = 1000
}

// Display helper: show #1/#2/#3 for 10/20/30
const getDisplayOrder = (seg: any) => {
  if (seg?.id === 'all') return 1000
  const pr = Number(seg?.priority)
  if (!Number.isFinite(pr) || pr <= 0) return '-'
  return Math.round(pr / 10)
}

// Disable helpers for arrows
const isTopSegment = (id: string) => {
  const nonAll = (segments.value as any[]).filter((s) => s.id !== 'all')
  return nonAll.length > 0 && nonAll[0]?.id === id
}
const isAboveAllSegment = (id: string) => {
  const nonAll = (segments.value as any[]).filter((s) => s.id !== 'all')
  return nonAll.length > 0 && nonAll[nonAll.length - 1]?.id === id
}

// Clickable row to toggle expand anywhere on the name cell
// no-op: expansion is handled by data table via expand-on-click

// Alternating background by order
const isEvenOrder = (seg: any) => {
  const order = getDisplayOrder(seg)
  if (order === 1000) return false
  return Number(order) % 2 === 0
}

// Human-readable preview for a single rule
const operatorLabelMap: Record<string, string> = {
  contains: 'includes',
  equals: 'equals',
  missing: 'is missing',
  not_contains: 'does not include',
  starts_with: 'starts with',
  ends_with: 'ends with',
  in: 'is in',
  not_in: 'is not in',
}

function previewCondition(rule: any): string {
  const field = rule?.field || 'Field'
  const opLabel = operatorLabelMap[String(rule?.operator ?? '')] || String(rule?.operator || '')
  const value = rule?.value
  if (rule?.operator === 'missing') {
    return `${field} ${opLabel}`
  }
  const printable = Array.isArray(value)
    ? JSON.stringify(value)
    : value
      ? `"${String(value)}"`
      : '"value"'
  return `${field} ${opLabel} ${printable}`
}

function getOperatorHelp(op: string): string {
  switch (op) {
    case 'contains':
      return 'Includes: field contains the given text (case-insensitive)'
    case 'equals':
      return 'Equals: field exactly matches the value'
    case 'missing':
      return 'Is Missing: field is empty or not present'
    case 'not_contains':
      return 'Does Not Include: field does not contain the given text'
    case 'starts_with':
      return 'Starts With: field begins with the given text'
    case 'ends_with':
      return 'Ends With: field ends with the given text'
    case 'in':
      return 'In List: field matches any of the provided values'
    case 'not_in':
      return 'Not In List: field matches none of the provided values'
    default:
      return 'Choose an operator to define how the value is matched'
  }
}

// New Segment Dialog methods
const openNewSegmentDialog = () => {
  newSegmentDialog.value = {
    show: true,
    name: '',
    error: '',
  }
}

const closeNewSegmentDialog = () => {
  newSegmentDialog.value = {
    show: false,
    name: '',
    error: '',
  }
}

const generateSegmentId = (name: string): string => {
  // Create a slug from the name: lowercase, replace spaces with underscores, remove special chars
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

const createNewSegment = () => {
  const name = newSegmentDialog.value.name.trim()

  if (!name) {
    newSegmentDialog.value.error = 'Segment name is required'
    return
  }

  const id = generateSegmentId(name)

  // Check if segment ID already exists
  const exists = segments.value.find((s: any) => s.id === id)
  if (exists) {
    newSegmentDialog.value.error = 'A segment with this name already exists'
    return
  }

  // Create new segment
  const nonAll = (segments.value as any[]).filter((s) => s.id !== 'all')
  const newPriority = nonAll.length > 0 ? Math.max(...nonAll.map((s) => s.priority || 0)) + 10 : 10

  const newSegment = {
    id,
    name,
    priority: newPriority,
    custom: true,
    action: '',
    criteria: [],
    feed: 'Chrome',
    filter: {},
  }

  // Insert before 'all' segment
  const allSeg = (segments.value as any[]).find((s) => s.id === 'all')
  if (allSeg) {
    segments.value = [...nonAll, newSegment, allSeg] as any
  } else {
    segments.value = [...nonAll, newSegment] as any
  }

  // Renumber priorities
  const updatedNonAll = (segments.value as any[]).filter((s) => s.id !== 'all')
  updatedNonAll.forEach((s, idx) => {
    s.priority = (idx + 1) * 10
  })

  // Close dialog
  closeNewSegmentDialog()

  // Auto-expand the new segment
  expandedRowIds.value = [id]

  // Show success message
  makeAlert(`Segment "${name}" created successfully`, 'success')
}

// Delete Segment Dialog methods
const confirmDeleteSegment = (segment: any) => {
  deleteSegmentDialog.value = {
    show: true,
    segmentId: segment.id,
    segmentName: segment.name,
  }
}

const closeDeleteSegmentDialog = () => {
  deleteSegmentDialog.value = {
    show: false,
    segmentId: '',
    segmentName: '',
  }
}

const deleteSegment = () => {
  const segmentId = deleteSegmentDialog.value.segmentId
  const segmentName = deleteSegmentDialog.value.segmentName

  // Remove the segment from the list
  segments.value = segments.value.filter((s: any) => s.id !== segmentId) as any

  // Renumber priorities for remaining segments
  const nonAll = (segments.value as any[]).filter((s) => s.id !== 'all')
  nonAll.forEach((s, idx) => {
    s.priority = (idx + 1) * 10
  })

  // Close dialog
  closeDeleteSegmentDialog()

  // Show success message
  makeAlert(`Segment "${segmentName}" deleted successfully`, 'success')
}
</script>

<style scoped>
.save-fab {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2000;
}

.v-data-table th {
  font-weight: bold;
  background-color: #f5f5f5;
}

/* Remove default row dividers so our custom row container controls separation */
.v-data-table tbody tr > td {
  border-bottom: 0;
}

/* Visual separation for All Vehicles segment */
.all-vehicles-row {
  border-top: 2px solid rgba(0, 0, 0, 0.08);
  background-color: rgba(0, 0, 0, 0.02);
}

.all-vehicles-row:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.segment-block {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  background: #fafafa;
}

.segment-alt {
  background: #f6f7f9;
}

.segment-header {
  border-bottom: 1px solid #eeeeee;
  color: #555;
  font-size: 0.9rem;
}

.segment-row {
  transition: background-color 120ms ease;
  padding: 10px 12px; /* make hover cover the full row area */
  margin: 6px -12px; /* add vertical breathing space; fill horizontally */
  border-radius: 6px;
  border: 1px solid transparent;
}
.segment-row:hover {
  background: #f7f7f7;
  cursor: pointer;
  border-color: #ececec;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

/* First-load pulse cue */
.pulse-highlight {
  animation: pulseFade 1.2s ease-in-out 1;
}
@keyframes pulseFade {
  0% {
    background: #f3f6ff;
  }
  60% {
    background: #ffffff;
  }
  100% {
    background: transparent;
  }
}

/* Delete button - neutral by default, red on hover (destructive action) */
.delete-btn {
  color: rgba(0, 0, 0, 0.6);
  border-color: rgba(0, 0, 0, 0.23);
  transition: all 0.2s ease;
}

.delete-btn:hover {
  color: #d32f2f !important;
  border-color: #d32f2f !important;
  background-color: rgba(211, 47, 47, 0.04) !important;
}
</style>
